import { PropsWithChildren, useEffect, useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import ExpandMoreIcon from "@mui/icons-material/ExpandMore"
import { Accordion, AccordionDetails, AccordionSummary, Typography } from "@mui/material"

const useStyles = makeStyles()((theme) => ({
    accordion: {
        padding: 0,
        borderRadius: 0,
        boxShadow: "none",
        border: "none",
        flex: "none",

        "&:first-of-type, &:last-of-type": {
            borderRadius: 0,
        },
        "&.Mui-expanded": {
            margin: 0,
        },
        "&:before": {
            display: "none",
        },
    },
    accordionSummary: {
        minHeight: "auto",
        padding: ".5rem",
        background: theme.palette.tailwind.slate[300],
        borderBottom: "1px solid rgba(0, 0, 0, 0.12)",
        "&.Mui-expanded": {
            minHeight: "auto",
        },
        "& .MuiAccordionSummary-content": {
            margin: 0,
            "&.Mui-expanded": {
                margin: 0,
            },
        },
    },

    accordionDetails: {
        textAlign: "left",
        padding: 0,
        marginTop: 0,
    },
}))

type ExtraServicesAccordionProps = Readonly<
    PropsWithChildren<{
        title: string
        defaultExpanded?: boolean
    }>
>

export function ExtraServicesAccordion({ title, defaultExpanded = false, children }: ExtraServicesAccordionProps) {
    const { classes } = useStyles()
    const [expanded, setExpanded] = useState(defaultExpanded)

    // Resync when defaultExpanded actually changes (e.g. an auto-added service appears after mount),
    // while still letting the user toggle the accordion in between.
    const prevDefaultExpanded = useRef(defaultExpanded)
    useEffect(() => {
        if (prevDefaultExpanded.current !== defaultExpanded) {
            prevDefaultExpanded.current = defaultExpanded
            setExpanded(defaultExpanded)
        }
    }, [defaultExpanded])

    const handleChange = () => {
        setExpanded((prev) => !prev)
    }

    return (
        <Accordion expanded={expanded} onChange={handleChange} className={classes.accordion}>
            <AccordionSummary className={classes.accordionSummary} expandIcon={<ExpandMoreIcon />}>
                <Typography variant="body1">{title}</Typography>
            </AccordionSummary>
            <AccordionDetails className={classes.accordionDetails}>{children}</AccordionDetails>
        </Accordion>
    )
}
